import { memo, useContext, useEffect } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import {
  ContentCard,
  LoadingSkeleton,
  ResponsiveFlex,
  RadioButtons,
  Row,
  Button,
} from "@narmi/design_system";
import { modules, useFeature } from "byzantine";
import {
  getWireTypeOptions,
  useCreateInternationalWireForm,
  useCurrencyContext,
  WireType,
  WireTypeEnum,
} from "byzantine/src/dbbl/businessLogic/modules/internationalWires";

import { Controller } from "react-hook-form";
import Container from "../../Container";
import InstitutionSettingsContext from "../../contexts/InstitutionSettingsContext";
import HeaderWithSteps from "../../HeaderWithSteps";
import * as formAdapters from "../../../src/adapters";
import styles from "./WireTransferContainer.module.scss";
import { useFormContainer } from "./hooks";
import InternationalWireContainer from "./InternationalWire/InternationalWireContainer";
import CurrencySelector from "./InternationalWire/CurrencySelector";

type WireTransferContainerProps = {
  setWireType: (wireType: WireType) => void;
};

const WireSelectScreen = ({ setWireType }: WireTransferContainerProps) => {
  const { featureEnabled } = useFeature();
  const {
    canSendInternationalWire,
    canSendDomesticWire,
  } = modules.wireTemplates.useWirePermissions();
  const { l10n } = useLocalization();
  const { wireType, onCancel, onSubmitMethod, form } = useFormContainer();
  const { control, watch, setError, clearErrors, setValue } =
    useCreateInternationalWireForm();
  const navigate = useNavigate();
  const {
    wire_display_fee: domesticWireFee,
    international_wire_fee: internationalWireFees,
  } = useContext(InstitutionSettingsContext);
  const { currencies, getCurrencies } = useCurrencyContext();
  const isOnlyUsd =
    currencies?.length === 1 && currencies[0].currency === "USD";

  const showWireTypeSelector =
    featureEnabled("wires") && canSendDomesticWire && canSendInternationalWire;

  useEffect(() => {
    if (isOnlyUsd) {
      setValue("recipientCurrency", currencies[0].currency);
      clearErrors("recipientCurrency");
    }
  }, [isOnlyUsd, currencies, setValue, clearErrors]);

  // if there's only one option, select it by default
  useEffect(() => {
    if (canSendDomesticWire && !canSendInternationalWire) {
      setWireType( WireTypeEnum.Domestic );
      form.setFieldValue("wireType", WireTypeEnum.Domestic, false);
    }
    if (!canSendDomesticWire && canSendInternationalWire) {
      setWireType( WireTypeEnum.International );
      form.setFieldValue("wireType", WireTypeEnum.International, false);
    }
  }, [setWireType, form.setFieldValue, canSendInternationalWire, canSendDomesticWire]);

  const onSubmit = () => {
    if (wireType === "domestic") {
      setWireType(wireType);
    } else if (wireType === "international") {
      if (!isOnlyUsd && currencies && currencies.length > 0 && !watch("recipientCurrency")) {
        setError("recipientCurrency", {
          type: "manual",
          message: l10n.getString("currency-selector-error-message"),
        });
        return;
      }
      clearErrors();
      setValue("hasFormBeenStarted", true);
      navigate("/transfer/wires/international");
    }
  };

  useEffect(() => {
    getCurrencies();
  }, [getCurrencies]);

  return (
    <ResponsiveFlex gapSize="xl">
      <HeaderWithSteps
        headerText={l10n.getString("heading-wire-title")}
      />
      {showWireTypeSelector && (<ContentCard kind="bordered">
        <p className={styles.wireContainerSubHeader}>Type</p>
        <RadioButtons
          name="options"
          alwaysShowDetails
          options={getWireTypeOptions(domesticWireFee, internationalWireFees)}
          {...formAdapters.radio(form, "wireType")}
        />
      </ContentCard>)}
      {wireType === "international" &&
        currencies &&
        currencies.length > 0 &&
        !isOnlyUsd && (
          <form>
            <Controller
              name={"recipientCurrency"}
              control={control as any}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CurrencySelector
                  currencies={currencies}
                  onChange={(e) => {
                    if (error) {
                      clearErrors("recipientCurrency");
                    }
                    onChange(e);
                  }}
                  value={value}
                  label={l10n.getString("label-currency-selector")}
                  error={error?.message || ""}
                />
              )}
            />
          </form>
        )}
      <Row alignItems="center">
        <Row.Item>
          <Button
            label="Cancel"
            testId="cancel-wire-transfer"
            kind="negative"
            onClick={onCancel}
          />
        </Row.Item>
        <Row.Item shrink>
          <Button
            label="Next"
            testId="submit-wire-transfer"
            onClick={!wireType ? onSubmitMethod : onSubmit}
          />
        </Row.Item>
      </Row>
    </ResponsiveFlex>
  );
};

const WireTransferContainer = memo(
  ({ setWireType }: WireTransferContainerProps) => {
    const { loading, load } = modules.internationalWires.bootstrap.useBootstrap();
    useEffect(() => {
      load();
    }, [load]);

    return (
      <Container>
        <LoadingSkeleton isLoading={loading}>
          <Routes>
            <Route
              path="/international/*"
              element={<InternationalWireContainer />}
            />
            <Route
              index
              element={<WireSelectScreen setWireType={setWireType} />}
            />
          </Routes>
        </LoadingSkeleton>
      </Container>
    );
  }
);

export default WireTransferContainer;
